import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomService } from '@app/custom/services/custom.service';
import { SaleType } from '@app/shared/models/sale-type';
import { WizardService } from '@app/wizard-bar/services/wizard.service';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { CommodityType } from '@app/shared/enums/commodity-type';
import LogRocket from 'logrocket';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { Address } from '@app/shared/models/address';
import { CustomerType } from '@app/shared/models/customer-type';
import { ProductTypeConfiguration } from '@configuration/models/product-type-configuration';
import { UtilityHubConfiguration } from '@configuration/models/utilityhub-configuration';
import { removeDuplicatePrimitives } from '@app/shared/helpers/array-helper';
import { defaultContractLengths } from '@app/shared/models/default-contract-lengths';
import { catchError, distinctUntilChanged, switchMap, take, tap } from 'rxjs/operators';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { SalesType } from '@app/shared/enums/sales-type';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { UtilityHubService } from '@app/shared/services/utilityhub.service';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector, configurationSelector, isReadOnlySelector, creditCheckSelector } from '@app/state/app.selectors';
import {
  SetQuoteRequestType,
  SetCustomerType,
  SetPageName,
  SetSalesType,
  SetSweetSpotCheckBoxState,
  SupplierUtilityInformation,
  SetCommodityType,
  SetProductType,
  SetUtilityUOM,
  ClearUtility,
  ClearUtilityInformation,
  SetUtilityInformation,
  SetUtility,
  SetContractLengths,
  SetBillingType,
} from '@utility-information/state/actions/utility-information.actions';
import {
  isDynamicSelector,
  brokerageNameSelector,
  zonesSelector,
  commodityTypeSelector,
  isCustomJourneyElectricEnabledSelector,
  isCustomJourneyGasEnabledSelector,
  isCustomJourneyEnabledSelector,
  quoteRequestStatusSelector,
  productTypesFromConfigSelector,
  billingTypeSelector,
  weatherZonesSelector,
} from '@utility-information/state/selectors/utility-information.selectors';

import { ShowControls } from '@app/shared/models/customer-details';
import { SupplierAssignedBrokerage, SupplierAssignedUser, SupplierModel} from '@app/shared/models/supplierModel';
import { ClearQuote, SetCreditCheckErrorMessage, SetCreditCheckStatus, SetCustomerDetails, SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { CreditCheckStatus } from '../../../shared/enums/credit-check-status';
import { CreditCheckResponse } from '../../../shared/models/credit-check';
import { CreditCheckInfo } from '../../../state/app.models';

@Component({
  selector: 'app-custom-customer-details',
  templateUrl: './custom-customer-details.component.html',
  styleUrls: ['./custom-customer-details.component.scss'],
})
export class CustomCustomerDetailsComponent implements OnInit, OnDestroy, AfterViewInit {

  quoteRequestId: any;
  frmCompanyDetails: FormGroup;

  utilities: string[];
  iso: string;
  zones: string[];
  weatherZones: string[];
  showControls: ShowControls = {
    showIso: true,
    showZone: true,
    showBundledRateCheckbox: false,
    loadingUtilities: false,
    emitEventOnValueChange: true,
  };

  loadingUtilities: boolean = false;
  emitEventOnValueChange = true;
  commodity: CommodityType;

  quoteRequestState;
  customerDetail: any;
  notes;
  showContractLengths: boolean = true;
  contractLengths;
  files;  
  defaultContractLengthButtonsToShow = defaultContractLengths;
  dunsEnabled;
  isQuickSubmission;
  companyDetails;
  userIdentity: UtilityHubIdentity;
  utilityHubConfiguration: UtilityHubConfiguration;
  showValidation: boolean = false;
  isRequestedStatus: boolean;

  uom: string;
  address: Address;
  externalId: string;
  billingType: string;
  supplierId: number;
  isGasEnabled: boolean = false;
  isElectricEnabled: boolean = false;
  isGasChecked: boolean = false;
  isElectricChecked: boolean = false;
  pricingSource: PricingSource;
  isReadOnly: boolean;

  billingType$: Subscription;
  saveCompanyDetails$: Subscription;
  utilities$: Subscription;
  saveCustomCompanyDetails$: Subscription;
  loadSubscription$: Subscription;
  routeSubscription$: Subscription;
  quoteRequestStatus$: Subscription;
  selectLoggedInUserSubscription$: Subscription;
  selectLoggedInUtilityHubIdentitySubscription$: Subscription;
  isReadOnly$: Subscription;
  creditCheck$: Subscription;

  isDynamic$: Observable<boolean>;
  assignedBrokerages$: Observable<SupplierAssignedBrokerage[]>;
  brokerageName$: Observable<string>;
  state$: Observable<string>;
  productTypes$: Observable<ProductTypeConfiguration[]>;
  contractLengthsFromProductType$: Observable<number[]>;
  zones$: Observable<string[]>;
  weatherZones$:Observable<string[]>;
  supplier$: Observable<SupplierModel>;
  supplierModel: SupplierModel;
  assignedBrokerages: SupplierAssignedBrokerage[];
  assignedSupplierUsers: SupplierAssignedUser[];
  creditCheck: CreditCheckInfo;

  resolveWaitForUploadConfirmation() {}
  onDeleteUpload(eventData) {}
  onCompleteUpload(eventData) {}
  formErrors(fieldName) {}

  constructor(
    private quoteService: CustomService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private wizardService: WizardService,
    private store: Store<AppState>,
    private quoteRequestService: QuoteRequestService,
    private pricingSourceService: PricingSourceService,
    private utilityHubService: UtilityHubService,
    private cd: ChangeDetectorRef
  ) {
    this.isDynamic$ = this.store.select(isDynamicSelector);
    this.brokerageName$ = store.select(brokerageNameSelector);
    this.zones$ = this.store.select(zonesSelector);
    this.weatherZones$ = this.store.select(weatherZonesSelector);
  }

  ngOnDestroy(): void {
    this.selectLoggedInUserSubscription$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentitySubscription$?.unsubscribe();
    this.quoteRequestStatus$?.unsubscribe();
    this.loadSubscription$?.unsubscribe();
    this.saveCompanyDetails$?.unsubscribe();
    this.utilities$?.unsubscribe();
    this.saveCustomCompanyDetails$?.unsubscribe();
    this.billingType$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
    this.creditCheck$.unsubscribe();
  }

  ngOnInit(): void {
    this.store.dispatch(new ClearQuote());
    this.wizardService.bindStep(this.route, (args) => from(this.save()));
    const saleType = new FormControl({ value: SaleType.Acquisition, disabled: false });
    this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId ?? 0, QuoteRequestType.Custom));
    this.store.dispatch(new SetCustomerType(this.quoteRequestId, CustomerType.Commercial));
    this.store.dispatch(new SetPageName(this.quoteRequestId, 'companydetails'));
    this.store.dispatch(new SetSalesType(this.quoteRequestId, SalesType.acquisition));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, true));

    this.selectLoggedInUserSubscription$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.selectLoggedInUtilityHubIdentitySubscription$ = this.store
          .select(loggedInUtilityHubIdentitySelector)
          .subscribe((uhUser) => {
            this.userIdentity = uhUser;
            this.supplierId = +uhUser?.currentOrganisation?.id;
          });
      }
    });

    this.billingType$ = this.store.select(billingTypeSelector).subscribe((billingType) => {
      this.billingType = billingType;
    });

    this.store.select(configurationSelector).subscribe((config) => {
      this.utilityHubConfiguration = config;
      this.dunsEnabled = config.supplierConfiguration.dunsLookupEnabled;
    });
    this.externalId = this.utilityHubService.getExternalId(this.userIdentity);
    this.supplier$ = this.utilityHubService.getSupplierByExternalId(this.externalId);
    this.supplier$.subscribe((supplier) => {
      this.assignedBrokerages = supplier.assignedBrokerages;
      this.assignedBrokerages.sort((a,b) => a.name.localeCompare(b.name));
      this.assignedSupplierUsers = supplier.assignedUsers
                                    .filter((x)=> x.role == UserRole.Supplier)
                                    .sort((a,b) => a.fullName.localeCompare(b.fullName));
    });
    this.creditCheck$ = this.store.select(creditCheckSelector).subscribe((cc) => {
      this.creditCheck = cc;
    });

    this.utilityHubService.getSupplierUtilityInformation(this.externalId).subscribe((resp) => {
      this.store.dispatch(new SupplierUtilityInformation(resp));
    });

    this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId, QuoteRequestType.Custom));
    this.store.dispatch(new SetCustomerType(this.quoteRequestId, CustomerType.Commercial));
    this.store.dispatch(new SetPageName(this.quoteRequestId, 'companydetails'));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, true));

    forkJoin([
      this.store.select(commodityTypeSelector).pipe(take(1)),
      this.store.select(isCustomJourneyElectricEnabledSelector).pipe(take(1)),
      this.store.select(isCustomJourneyGasEnabledSelector).pipe(take(1)),
      this.isDynamic$.pipe(take(1)),
      this.route.params.pipe(take(1)),
      this.store.select(isCustomJourneyEnabledSelector).pipe(take(1)),
    ])
      .pipe(
        take(1),
        tap((arr) => {
          const commodityType = arr[0];
          const allowCustomElectric = arr[1];
          const allowCustomGas = arr[2];
          const isDynamic = arr[3];
          const quoteRequestId = arr[4].quoteRequestId;
          const allowCustom = arr[5];

          // Fix: Assign the quoteRequestId from route parameters to component property
          console.log('Route parameters:', arr[4], 'quoteRequestId extracted:', quoteRequestId);
          this.quoteRequestId = !isNaN(+quoteRequestId) ? +quoteRequestId : quoteRequestId;
          console.log('Final quoteRequestId assigned:', this.quoteRequestId);

          if (this.userIdentity.isSupplierManagerOrSalesManager && isDynamic === true) {
            if (commodityType) {
              if (commodityType === CommodityType.Gas && allowCustomGas === true) {
                this.isGasEnabled = true;
                this.isGasChecked = true;
                this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Gas));
                this.commodity = CommodityType.Gas;
              } else if (commodityType === CommodityType.Electricity && allowCustomElectric === true) {
                this.isElectricEnabled = true;
                this.isElectricChecked = true;
                this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Electricity));
                this.commodity = CommodityType.Electricity;
              }
            } else {
              //refresh browser
              this.route.queryParams
                .subscribe((x) => {
                  if (x.commodityType) {
                    if (x.commodityType == CommodityType.Gas) {
                      this.isGasEnabled = true;
                      this.isGasChecked = true;
                      this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Gas));
                      this.commodity = CommodityType.Gas;
                    } else {
                      this.isElectricEnabled = true;
                      this.isElectricChecked = true;
                      this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Electricity));
                      this.commodity = CommodityType.Electricity;
                    }
                  }
                })
                .unsubscribe();
            }
          } else {
            this.isGasEnabled = allowCustomGas;
            this.isElectricEnabled = allowCustomElectric;
            if (quoteRequestId == 'new') {
              this.isElectricChecked = allowCustomElectric;
              this.isGasChecked = !this.isElectricChecked && allowCustomGas;
              if (this.isElectricChecked) {
                this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Electricity));
                this.commodity = CommodityType.Electricity;
              } else {
                this.store.dispatch(new SetCommodityType(quoteRequestId, CommodityType.Gas));
                this.commodity = CommodityType.Gas;
              }
            }
          }

          this.pricingSource = this.pricingSourceService.fetchPricingSource(
            QuoteRequestType.Custom,
            UserRole[this.userIdentity.currentOrganisation.role],
            isDynamic,
            CommodityType[this.commodity.toLowerCase()]
          );
          if (
            allowCustom === true &&
            this.userIdentity.isSupplierManagerOrSalesManager &&
            this.pricingSource === PricingSource.ManualEntry
          ) {
            this.showContractLengths = false;
          }
        }),
        catchError((err) => {
          return of(err);
        })
      )
      .subscribe();

    this.quoteRequestStatus$ = this.store.select(quoteRequestStatusSelector).subscribe((x) => {
      this.isRequestedStatus = x !== QuoteRequestStatus.InProgress;
    });

    this.frmCompanyDetails = this.fb.group({
      brokerage: [''],
      broker: [''],
      customerDetails: [''],
      notes: ['', Validators.maxLength(255)],
      sweetSpotCheckbox: [{ value: true, disabled: false }],
      contractLengths: [{ value: '' }],
      productType: ['', Validators.required],
      commodity: this.isElectricChecked ? [CommodityType.Electricity] : [CommodityType.Gas],
      // utilityDetails: [''],
      saleType,
    });



    this.routeSubscription$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = x.quoteRequestId;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      }
    });

    this.route.queryParams.subscribe((x) => (this.isQuickSubmission = x.quickSubmission)).unsubscribe();

    this.productTypes$ = this.store.select(productTypesFromConfigSelector).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev.map((x) => x)) === JSON.stringify(curr.map((y) => y))),
      switchMap((products) => {
        if (products?.length === 1) {
          this.selectSingleProduct(products[0]);
          return of(products);
        }

        if (!this.isReadOnly) {
          this.productTypeCtrl.enable();
        }
        return of(products);
      })
    );

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
      if (this.isReadOnly) {
        this.frmCompanyDetails.disable({ emitEvent: false });
      }
    });
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  get brokerageCtrl() {
    return this.frmCompanyDetails?.get('brokerage');
  }

  get customerDetailsCtrl() {
    return this.frmCompanyDetails?.get('customerDetails');
  }

  get sweetSpotCheckboxCtrl() {
    return this.frmCompanyDetails?.get('sweetSpotCheckbox');
  }

  get commodityCtrl() {
    return this.frmCompanyDetails?.get('commodity');
  }

  get contractLengthsCtrl() {
    return this.frmCompanyDetails?.get('contractLengths');
  }

  public get productTypeCtrl() {
    return this.frmCompanyDetails.get('productType');
  }

  isQuoteRequestInvalidated() {
    return this.frmCompanyDetails?.invalid && this.isRequestedStatus && this.quoteRequestId && this.quoteRequestId !== 'new';
  }

  isSupplierManagerManualEntry(): Observable<boolean> {
    return of(true);
  }

  productSelectionChanged(productType: any) {
    this.store.dispatch(new SetProductType(this.quoteRequestId, productType));
  }

  selectSingleProduct(productType: ProductTypeConfiguration) {
    if (productType) {
      this.productTypeCtrl.setValue(productType.productType);
      this.productTypeCtrl.disable();
      this.productSelectionChanged(productType.productType);
    }
  }

  load() {
    this.loadSubscription$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      formData: this.quoteService.getCompanyDetails(this.quoteRequestId),
    }).subscribe((res) => {
      this.store.dispatch(new SetCustomerType(this.quoteRequestId, res.formData?.customerDetails?.customerType));
      this.store.dispatch(new SetSalesType(this.quoteRequestId, res.formData?.saleType));
      this.store.dispatch(new SetProductType(this.quoteRequestId, res.formData.productType));
      this.store.dispatch(new SetBillingType(this.quoteRequestId, res.formData.customerDetails.billingType));
      this.store.dispatch(new SetQuote(res.state, this.userIdentity, res.state.hasIDontKnows));
      this.store.dispatch(new SetCustomerDetails(this.quoteRequestId, res.formData?.customerDetails));
      this.store.dispatch(new SetCreditCheckStatus(this.quoteRequestId, res.state.creditCheckStatus));
      this.store.dispatch(new SetCreditCheckErrorMessage(this.quoteRequestId, ""));

      this.address = res.formData?.customerDetails?.location;

      if (!this.isReadOnly) {
        this.sweetSpotCheckboxCtrl.enable();
      }

      let companyDetails = { ...res.formData };
      // patchValue does not work for object that have properties that are not on the form
      this.companyDetails = res.formData;
      this.commodity = res.formData.commodity;
      this.frmCompanyDetails.patchValue(companyDetails, { emitEvent: true });
      this.files = res.formData.loaDocuments;
      this.quoteRequestState = res.state;
      this.emitEventOnValueChange = false;
      this.isGasChecked = this.commodity == CommodityType.Gas;
      this.isElectricChecked = this.commodity == CommodityType.Electricity;
      this.commodityCtrl.setValue(this.commodity);

      this.store.dispatch(
        new SetUtilityInformation(this.quoteRequestId, this.address.state, this.quoteRequestState.quoteRequestType, this.address.zipCode, this.commodity)
      );
    });
  }

  storeUtilityInformation(address: Address) {
    this.address = address;
    this.store.dispatch(new ClearUtility(this.quoteRequestId));
    this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
    this.store.dispatch(
      new SetUtilityInformation(this.quoteRequestId, address.state, QuoteRequestType.Custom, address.zipCode, this.commodityCtrl.value)
    );
    if (this.quoteRequestId != "new")
      this.store.dispatch(new SetUtility(this.quoteRequestId, this.utilities[0]));

  }

  allContractLengthsSelected(selected: any) {
    this.frmCompanyDetails.get('sweetSpotCheckbox').patchValue(selected);

    if (!this.isReadOnly) {
      this.frmCompanyDetails.get('sweetSpotCheckbox').enable();
    }
  }

  sweetSpotCheckboxChanged(event) {
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, event.checked));
  }

  public next() {
    this.showValidation = true;
    this.frmCompanyDetails.markAllAsTouched();
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  public commodityChanged(commodity: any) {
    this.commodity = <CommodityType>commodity.value;
    this.showControls = { ...this.showControls };
    this.store.dispatch(new SetCommodityType(this.quoteRequestId, <CommodityType>commodity.value));
    if (this.address) {
      this.storeUtilityInformation(this.address);
    }
  }

  async save(): Promise<any> {

    if (this.frmCompanyDetails.disabled) {
      return true;
    }

    if (this.frmCompanyDetails.pristine && this.frmCompanyDetails.valid && this.quoteRequestId !== 'new') {
      return true;
    }

    if (this.frmCompanyDetails.valid) {
      const configValues = this.frmCompanyDetails.getRawValue();

    const matchedBroker = this.assignedSupplierUsers.find(x => x.supplierAgentCode === configValues.broker);

    if (matchedBroker) {
      configValues.brokerName = matchedBroker.fullName;
    }
      const customerDetails = {
        ...this.frmCompanyDetails?.getRawValue(),
        loaDocuments: this.customerDetail?.files,
        quoteRequestId: !isNaN(+this.quoteRequestId) && this.quoteRequestId ? +this.quoteRequestId : 0,
        brokerName: configValues.brokerName,
      };
      console.log('About to save - this.quoteRequestId:', this.quoteRequestId, 'customerDetails.quoteRequestId:', customerDetails.quoteRequestId);
      console.log(customerDetails, '<== Customer details')
      const quoteData = {
        ...this.frmCompanyDetails.getRawValue(),
        quoteRequestId: this.quoteRequestId,
        sweetSpotCheckbox: this.sweetSpotCheckboxCtrl.value,
      };
      console.log(quoteData, '<== Quote Data')

      if (customerDetails?.contractLengths?.length === undefined) customerDetails.contractLengths = this.defaultContractLengthButtonsToShow;

      if (quoteData?.contractLengths?.length === undefined) quoteData.contractLengths = this.defaultContractLengthButtonsToShow;

      customerDetails.contractLengths = removeDuplicatePrimitives(customerDetails.contractLengths);
      quoteData.contractLengths = removeDuplicatePrimitives(customerDetails.contractLengths);

      quoteData.customerDetails.billingType = this.billingType;

      if (this.creditCheck?.status) {      
        const creditCheck: CreditCheckResponse = { status: this.creditCheck.status as CreditCheckStatus, errorMessage: this.creditCheck.errorMessage, requestDate: undefined };
        quoteData.creditCheckDetails = creditCheck;
      }
      
      this.saveCompanyDetails$ = this.quoteService.saveCompanyDetails(customerDetails.quoteRequestId, customerDetails).subscribe((x) => {
        quoteData.quoteRequestId = x;
        this.quoteService.saveQuoteConfiguration(x, quoteData).subscribe((y) => {
          this.store.dispatch(new SetContractLengths(this.quoteRequestId, quoteData.contractLengths));
          if (!customerDetails.quoteRequestId) {
            LogRocket.track('new_custom_quote');
            this.router
              .navigate(['custom', quoteData.quoteRequestId, CustomConstants.wizardSteps[0].slug], {
                queryParams: { commodityType: quoteData.commodity },
              })
              .then(() => {
                this.route.params.subscribe(() => {
                  this.wizardService.bindStep(this.route, () => of(true)); // call again as we have just changed the route
                  this.wizardService.next();
                });
              });
          } else {
            this.router
              .navigate(['custom', quoteData.quoteRequestId, CustomConstants.wizardSteps[0].slug], {
                queryParams: { commodityType: quoteData.commodity },
              })
              .then(() => {
                this.route.params.subscribe(() => {
                  this.wizardService.bindStep(this.route, () => of(true)); // call again as we have just changed the route
                  this.wizardService.next();
                });
              });
          }
          return true;
        });
      });
    }
  }

  public utilityUomSelected($event) {
    this.uom = $event;
  }

  public utilitySelected(utility: string) {
    this.zones$.subscribe((x) => (this.zones = x));
    this.weatherZones$.subscribe((x)=>{this.weatherZones = x;});
  }
}
